// Organization structured data for Karyfuw Supply Chain
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'EasyBuy General Trading',
  description: 'Leading supply chain management and global trading solutions across multiple sectors with over 20 years of experience.',
  url: 'https://easybuytrd.com',
  logo: 'https://easybuytrd.com/logo.png',
  foundingDate: '2003',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'Global Operations'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    availableLanguage: ['English']
  },
  sameAs: [
    // Add social media URLs when available
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Supply Chain Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Global Trading Solutions',
          description: 'Comprehensive trading services across international markets'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Supply Chain Management',
          description: 'End-to-end supply chain optimization and management'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Logistics Solutions',
          description: 'Efficient logistics and distribution services'
        }
      }
    ]
  }
};

// Website structured data
const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'EasyBuy General Trading',
  url: 'https://easybuytrd.com',
  description: 'Leading supply chain management and global trading solutions',
  publisher: {
    '@type': 'Organization',
    name: 'EasyBuy General Trading'
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://easybuytrd.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
};

// Breadcrumb structured data generator
const generateBreadcrumbSchema = (pathname: string) => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://easybuytrd.com/'
    }
  ];

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const name = segment.charAt(0).toUpperCase() + segment.slice(1);
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: index + 2,
      name,
      item: `https://easybuytrd.com${currentPath}`
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems
  };
};

// Service pages structured data
const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Supply Chain Management Services',
  provider: {
    '@type': 'Organization',
    name: 'EasyBuy General Trading'
  },
  description: 'Comprehensive supply chain management services across multiple sectors',
  serviceType: 'Supply Chain Management',
  areaServed: 'Worldwide',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Sector-Specific Solutions',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Agriculture & Food',
          description: 'Specialized supply chain solutions for agricultural and food industries'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Manufacturing',
          description: 'Industrial manufacturing supply chain optimization'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Technology',
          description: 'Technology sector supply chain management'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Healthcare',
          description: 'Healthcare industry supply chain solutions'
        }
      }
    ]
  }
};

// Contact page structured data
const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact EasyBuy General Trading',
  description: 'Get in touch with EasyBuy General Trading for your global trading and supply chain management needs',
  mainEntity: {
    '@type': 'Organization',
    name: 'EasyBuy General Trading',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      availableLanguage: ['English']
    }
  }
};

// Main function to get structured data based on pathname
export function getStructuredData(pathname: string): object {
  const baseSchemas: object[] = [organizationSchema, websiteSchema];

  // Add breadcrumb for non-home pages
  if (pathname !== '/') {
    baseSchemas.push(generateBreadcrumbSchema(pathname));
  }

  // Add page-specific schemas
  switch (pathname) {
    case '/':
      return {
        '@context': 'https://schema.org',
        '@graph': baseSchemas
      };

    case '/sectors':
      return {
        '@context': 'https://schema.org',
        '@graph': [...baseSchemas, servicesSchema]
      };

    case '/contact':
      return {
        '@context': 'https://schema.org',
        '@graph': [...baseSchemas, contactSchema]
      };

    case '/about':
      return {
        '@context': 'https://schema.org',
        '@graph': baseSchemas
      };

    default:
      return {
        '@context': 'https://schema.org',
        '@graph': baseSchemas
      };
  }
}